/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable consistent-return */
import * as React from 'react';
import classNames from 'classnames';
import { Control, useController, FieldErrors } from 'react-hook-form';
import Label from '../Label';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import FormHelperText from '../FormHelperText';
import SelectOption, { Option, UniqOption } from './SelectOption';
import SearchBar from '../SearchBar';

type FocusOptionDirection = 'down' | 'first' | 'last' | 'up';

type SelectSize = 'large';

type ValueType = string | number | Array<string | number>;

interface OptionGroup {
  readonly options: readonly Option[];
  readonly label?: string;
}

const getKey = (item: unknown, index: number) => {
  /*
    if object has been passed for react key, 
    use index number instead
  */
  return typeof item === 'object' ? index : item;
};

export interface SelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: Array<Option | OptionGroup>;
  disabled?: boolean;
  multiple?: boolean;
  control?: Control;
  rules?: object;
  formGroupClassName?: string;
  labelClassName?: string;
  selectClassName?: string;
  size?: SelectSize;
  errors?: FieldErrors;
  inRow?: boolean;
  inputIcon?: React.ReactNode;
  onChange?: (selectedValue: string | string[]) => void;
  autoWidth?: boolean;
  disableSelectedOption?: boolean;
  hasSearchBar?: boolean;
  handleSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder?: string;
  fixedBottomComponent?: React.ReactNode;
  forceValue?: ValueType;
}

function Select(props: SelectProps) {
  const {
    size,
    name,
    label,
    rules = {},
    options,
    disabled,
    control,
    placeholder,
    multiple = false,
    formGroupClassName,
    labelClassName,
    selectClassName,
    errors = {},
    inRow,
    inputIcon,
    onChange: onSelectChange,
    autoWidth,
    disableSelectedOption = false,
    hasSearchBar = false,
    handleSearch,
    searchPlaceholder,
    fixedBottomComponent,
    forceValue,
  } = props;
  const {
    field: { value: valueProp, onChange, onBlur },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    meta: { invalid },
  } = useController({
    name,
    rules,
    control,
  });
  const computedOptions = React.useMemo(
    () =>
      options.reduce((acc, item, groupIndex) => {
        const newItem =
          'options' in item
            ? item.options.map((option, optionIndex) => ({
                ...option,
                key: `${groupIndex}-${optionIndex}`,
              }))
            : [{ ...item, key: item.value }];
        return [...acc, ...newItem];
      }, []),
    [options]
  );

  const formatDefaultValue = (defaultVal?: ValueType) => {
    const findOptionByValue = (
      options: Array<Option>,
      value?: ValueType
    ): Option => options.find((option: Option) => option.value === value)!;

    if (multiple && Array.isArray(defaultVal)) {
      return defaultVal.map((val: string) =>
        findOptionByValue(computedOptions, val)
      );
    }
    return findOptionByValue(computedOptions, defaultVal);
  };

  const defaultValue = formatDefaultValue(valueProp);
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [value, setValue] = React.useState<Option | Option[] | undefined>(
    defaultValue as Option | Option[] | undefined
  );
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const [focusedOption, setFocusedOption] = React.useState<UniqOption | null>(
    null
  );
  const inputRef = React.useRef<HTMLSpanElement>(null);
  const menuListRef = React.useRef<HTMLUListElement | HTMLDivElement>(null);
  const searchRef = React.useRef<any>(null);
  const bottomComponentRef = React.useRef<any>(null);
  let focusedOptionRef = React.useRef<HTMLLIElement>();
  const inputClasses = classNames('ui-select-input');
  const selectFieldClasses = classNames('ui-form-group', formGroupClassName, {
    'ui-form-group--row': inRow,
  });
  const wrapperClasses = classNames('ui-select-wrapper', {
    'ui-select-wrapper--has-icon': inputIcon,
  });
  const selectClasses = classNames('ui-select', selectClassName, {
    'ui-select--autowidth': autoWidth,
  });
  const [isTrimmed, setTrimmed] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (menuListRef.current && focusedOptionRef.current) {
      scrollIntoView(menuListRef.current, focusedOptionRef.current);
    }
  }, [focusedOption]);

  React.useEffect(() => {
    if (forceValue) {
      setValue(formatDefaultValue(forceValue));
    }
  }, [forceValue, options]);

  React.useEffect(() => {
    setTrimmed(inputRef!.current!.offsetWidth < inputRef!.current!.scrollWidth);
  });

  const scrollIntoView = (
    menuEl: HTMLDivElement | HTMLUListElement,
    focusedEl: HTMLLIElement
  ) => {
    const menuRect = menuEl.getBoundingClientRect();
    const focusedRect = focusedEl.getBoundingClientRect();
    const overScroll = focusedEl.offsetHeight / 3;

    if (focusedRect.bottom + overScroll > menuRect.bottom) {
      // eslint-disable-next-line no-param-reassign
      menuEl.scrollTop = Math.min(
        focusedEl.offsetTop +
          focusedEl.clientHeight -
          menuEl.offsetHeight +
          overScroll,
        menuEl.scrollHeight
      );
    } else if (focusedRect.top - overScroll < menuRect.top) {
      // eslint-disable-next-line no-param-reassign
      menuEl.scrollTop = Math.max(focusedEl.offsetTop - overScroll, 0);
    }
  };

  const handleClick = () => {
    setOpen(!isOpen);
    setFocusedOption(null);
    setIsFocused(true);
  };

  const handleArrowMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    setIsFocused(true);

    setOpen(!isOpen);
    setFocusedOption(null);
  };

  const handleInputBlur = () => {
    if (inputRef.current) {
      if (inputRef.current.contains(document.activeElement)) {
        return;
      }
    }

    if (searchRef.current) {
      return;
    }
    if (bottomComponentRef.current) {
      return;
    }

    if (onBlur) {
      onBlur();
    }

    setIsFocused(false);
    setOpen(false);
  };

  const focusOption = (direction: FocusOptionDirection = 'first') => {
    if (!options.length) return;

    let nextFocus = 0; // handles 'first'
    const focusedIndex = computedOptions.findIndex(
      (option: UniqOption) => option.key === focusedOption?.key
    );

    if (direction === 'up') {
      nextFocus =
        focusedIndex > 0 ? focusedIndex - 1 : computedOptions.length - 1;
    } else if (direction === 'down') {
      nextFocus = (focusedIndex + 1) % computedOptions.length;
    } else if (direction === 'last') {
      nextFocus = computedOptions.length - 1;
    }

    setFocusedOption(computedOptions[nextFocus]);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        if (isOpen) {
          setOpen(false);
        }

        break;
      case 'Enter':
        if (isOpen) {
          if (!focusedOption) return;

          event.preventDefault();
          handleOptionClick(focusedOption);
          break;
        }

        return;
      case 'ArrowUp':
        if (isOpen) {
          focusOption('up');
        } else {
          setOpen(true);
          focusOption('last');
        }

        break;
      case 'ArrowDown':
        if (isOpen) {
          focusOption('down');
        } else {
          setOpen(true);
          focusOption('first');
        }

        break;
      case ' ': // space
        if (!isOpen) {
          setOpen(true);
          focusOption('first');
        }

        break;
      default:
        break;
    }
  };

  const handleOptionClick = React.useCallback(
    (option: Option) => {
      let newValue: React.SetStateAction<Option | Option[]>;

      if (multiple) {
        newValue = Array.isArray(value) ? [...value] : [];
        const itemIndex = (newValue as Option[]).findIndex(
          (item) => item?.value === option.value
        );

        if (itemIndex === -1) {
          newValue.push(option);
        } else {
          newValue.splice(itemIndex, 1);
        }
      } else {
        newValue = option;
      }

      const selectedValue = multiple
        ? (newValue as Option[]).map((item) => item?.value)
        : (newValue as Option).value;

      if (onChange) {
        onChange(selectedValue);
      }

      // custom onChange
      if (onSelectChange) {
        onSelectChange(selectedValue as string | string[]);
      }

      setValue(newValue);

      if (!multiple) {
        setOpen(false);
      }
    },
    [value, onChange, onSelectChange, multiple]
  );

  const onMenuMouseDown = (
    event: React.MouseEvent<HTMLDivElement | HTMLUListElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setIsFocused(true);
  };

  const getFocusedOptionRef = (ref: HTMLLIElement) => {
    focusedOptionRef = { current: ref };
  };

  const formatValue = () => {
    if (!value) {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => item && item.label)
        .filter((item) => item !== undefined)
        .join(', ');
    }
    return (value as Option).label;
  };
  const handleClickOutside = React.useCallback(
    (event) => {
      const needToBeOpen =
        inputRef?.current?.contains(event.target) ||
        menuListRef?.current?.contains(event.target) ||
        searchRef?.current?.contains(event.target) ||
        bottomComponentRef?.current?.contains(event.target);
      if (isOpen && !needToBeOpen) {
        setIsFocused(false);
        setOpen(false);
      }
    },
    [isOpen]
  );
  const MenuListComponent =
    options.length !== 0 && 'options' in options[0] ? 'div' : 'ul';
  const formattedValue = formatValue();
  const displayPlaceholder = !formattedValue;
  const valueClasses = classNames('ui-select-value', {
    'ui-select-value--placeholder': displayPlaceholder,
    'ui-select-value--focused': isFocused,
    'ui-select-value--disabled': disabled,
    [`ui-select-value--${size}`]: size,
  });

  React.useEffect(() => {
    isOpen && document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  return (
    <div className={selectFieldClasses}>
      {label && <Label id={name} label={label} className={labelClassName} />}
      <div>
        <div className={selectClasses}>
          <div
            role="combobox"
            aria-controls={`${name}-listbox`}
            aria-expanded={isOpen}
            aria-owns={`${name}-listbox`}
            aria-haspopup="listbox"
            id={`${name}-combobox`}
            className={wrapperClasses}
          >
            {inputIcon}
            <span
              role="textbox"
              tabIndex={0}
              ref={inputRef}
              aria-autocomplete="list"
              aria-controls={`${name}-listbox`}
              aria-labelledby={`${name}-label`}
              className={valueClasses}
              onClick={disabled ? undefined : handleClick}
              onKeyDown={handleKeyDown}
              onBlur={handleInputBlur}
            >
              {isTrimmed && (
                <Tooltip
                  content={formattedValue || placeholder}
                  tabIndex={-1}
                  size="large"
                  theme="light"
                  pointerPosition="top-left"
                  debounceMs={0}
                  hideTimeout={0}
                >
                  {''}
                </Tooltip>
              )}
              {formattedValue || placeholder}
            </span>

            <input
              value={formattedValue as string}
              readOnly={true}
              className={inputClasses}
              id={`${name}`}
              disabled={disabled}
              placeholder={placeholder}
            />

            <div
              onMouseDown={disabled ? undefined : handleArrowMouseDown}
              className="ui-select-dropdown-icon"
              id={`${name}-combobox-arrow`}
              role="button"
              tabIndex={-1}
              aria-label={label}
            >
              <Icon name="chevron-down" size="x-small" />
            </div>
          </div>
          {isOpen && (
            <div
              className={classNames('ui-select-list-wrapper', {
                'ui-select-list-wrapper--open': isOpen,
              })}
            >
              {hasSearchBar && (
                <div className="ui-select-searchbar">
                  <SearchBar
                    onChange={handleSearch}
                    placeholder={searchPlaceholder}
                    innerRef={searchRef}
                  />
                </div>
              )}

              <MenuListComponent
                // @ts-expect-error
                ref={menuListRef}
                id={`${name}-listbox`}
                role="listbox"
                aria-labelledby={`${name}-label`}
                className="ui-select-listbox"
                onMouseDown={onMenuMouseDown}
              >
                {options.map((option: Option | OptionGroup, groupIndex) => {
                  if ('options' in option) {
                    return (
                      <ul
                        key={`${option.label}-${groupIndex}`}
                        role="group"
                        aria-labelledby={option.label}
                        className="ui-select-group"
                      >
                        <li
                          className="ui-select-group-label"
                          role="presentation"
                          id={option.label}
                        >
                          {option.label}
                        </li>
                        {option.options.map((opt: Option, optionIndex) => {
                          const selected = Array.isArray(value)
                            ? value.findIndex(
                                (item) => item?.value === opt.value
                              ) > -1
                            : value?.value === opt.value;
                          const isOptionFocused =
                            focusedOption?.key ===
                            `${groupIndex}-${optionIndex}`;
                          return (
                            <SelectOption
                              key={`${groupIndex}-${optionIndex}`}
                              selected={
                                disableSelectedOption ? false : selected
                              }
                              isFocused={isOptionFocused}
                              handleOptionClick={handleOptionClick}
                              innerRef={
                                isOptionFocused
                                  ? getFocusedOptionRef
                                  : undefined
                              }
                              isMulti={multiple}
                              {...opt}
                            />
                          );
                        })}
                      </ul>
                    );
                  }

                  const selected = Array.isArray(value)
                    ? value.findIndex((item) => item?.value === option.value) >
                      -1
                    : value?.value === option.value;
                  const isOptionFocused = focusedOption?.key === option.value;

                  return (
                    <SelectOption
                      key={`${getKey(option.value, groupIndex)}`}
                      selected={disableSelectedOption ? false : selected}
                      isFocused={isOptionFocused}
                      handleOptionClick={handleOptionClick}
                      innerRef={
                        isOptionFocused ? getFocusedOptionRef : undefined
                      }
                      isMulti={multiple}
                      {...option}
                    />
                  );
                })}
              </MenuListComponent>
              {fixedBottomComponent && (
                <div
                  className="ui-select-bottomComponent"
                  ref={bottomComponentRef}
                >
                  {fixedBottomComponent}
                </div>
              )}
            </div>
          )}
        </div>
        {invalid && (
          <FormHelperText
            text={errors[name]?.message}
            hasError={Boolean(errors[name])}
          />
        )}
      </div>
    </div>
  );
}

export default Select;
